import type { CSSProperties, ComponentProps, PropsWithChildren } from 'react';
import { useRef } from 'react';
import styled from 'styled-components';

import { Bezel } from '#src/atoms/ui/bezels/Bezel/index.tsx';
import type { HexColor } from '#src/style/types.ts';

const MouseHoverLayer = styled.div`
  position: absolute;
  inset: 0;
  mix-blend-mode: color;
  opacity: 0;
  background: radial-gradient(
    var(--size) var(--size) at var(--mx) var(--my),
    var(--color),
    rgba(0, 0, 0, 0)
  );

  *:hover > & {
    opacity: 1;
  }
  transition: opacity 300ms ease-in-out;
`;

type Props = {
  mouseHoverEffect?: {
    enabled: boolean;
    color: HexColor;
    size: number;
  };
} & ComponentProps<typeof Bezel>;
export const DotsBackgroundBezel = ({
  mouseHoverEffect = { enabled: true, color: '#F258E3', size: 50 },
  children,
  ...rest
}: PropsWithChildren<Props>) => {
  const mouseLayerRef = useRef<HTMLDivElement | null>(null);

  return (
    <Bezel
      background="conic-gradient(from 90deg at 3px 3px, var(--c-nd-100) 25%, var(--c-black) 0) 0 0/5px 5px"
      onMouseMove={
        mouseHoverEffect.enabled
          ? ({ clientX, clientY }) => {
              const rootClientRect =
                mouseLayerRef.current?.getBoundingClientRect();
              if (rootClientRect) {
                if (mouseLayerRef.current) {
                  mouseLayerRef.current.style.setProperty(
                    '--mx',
                    `${((clientX - rootClientRect.left) * 100) / rootClientRect.width}%`
                  );
                  mouseLayerRef.current.style.setProperty(
                    '--my',
                    `${((clientY - rootClientRect.top) * 100) / rootClientRect.height}%`
                  );
                }
              }
            }
          : undefined
      }
      {...rest}
    >
      {!!mouseHoverEffect.enabled && (
        <MouseHoverLayer
          ref={mouseLayerRef}
          style={
            {
              '--color': mouseHoverEffect.color,
              '--size': `${mouseHoverEffect.size}px`,
            } as CSSProperties
          }
        />
      )}
      {children}
    </Bezel>
  );
};
